import { Box } from '@mui/material';

import { MannWhitneyResultsItem } from '../../types/state';
import { formatNumberFixedTwo } from '../../utils/format';

const METRIC_HEADERS = ['Metric', 'Base', 'New', 'Interpretation'];

// Format a stat with thousands grouping and 2 decimals, or "N/A" when the value is missing.
const formatStat = (value: number | null | undefined) =>
  value != null ? formatNumberFixedTwo(value) : 'N/A';

interface MannWhitneyCompareMetricsProps {
  result: MannWhitneyResultsItem;
}

export const MannWhitneyCompareMetrics = ({
  result,
}: MannWhitneyCompareMetricsProps) => {
  if (!result) {
    return null;
  }

  const {
    mean: baseMean,
    median: baseMedian,
    variance: baseVariance,
    stddev: baseStandardDev,
    min: baseMin,
    max: baseMax,
  } = result?.base_standard_stats ?? {
    mean: null,
    median: null,
    variance: null,
    stddev: null,
    min: null,
    max: null,
  };
  const {
    mean: newMean,
    median: newMedian,
    variance: newVariance,
    stddev: newStandardDev,
    min: newMin,
    max: newMax,
  } = result?.new_standard_stats ?? {
    mean: null,
    median: null,
    variance: null,
    stddev: null,
    min: null,
    max: null,
  };
  const baseShapiroWilkPVal = result.shapiro_wilk_test_base?.pvalue ?? 'N/A';
  const newShapiroWilkPVal = result.shapiro_wilk_test_new?.pvalue ?? 'N/A';
  const baseShapiroWilkInterpretation =
    result.shapiro_wilk_test_base?.interpretation ?? 'N/A';
  const newShapiroWilkInterpretation =
    result.shapiro_wilk_test_new?.interpretation ?? 'N/A';

  return (
    <Box
      sx={{
        backgroundColor: 'manWhitneyComps.compareMetricsBg',
        marginBottom: 2,
        maxWidth: '85%',
        width: '100%',
        borderRadius: '5px',
        padding: 2,
        '& .test-row-container': {
          gridTemplateColumns: '1.5fr 1fr 1fr 2fr',
          display: 'grid',
          gap: 2,
        },
        '& .test-label-row': {
          fontWeight: 'bold',
          width: '100%',
        },
      }}
    >
      <table
        style={{
          display: 'grid',
          grid: '1fr',
          gap: 2,
          flexDirection: 'column',
        }}
      >
        <thead>
          <tr className='test-row-container'>
            {METRIC_HEADERS.map((header) => (
              <th
                key={header}
                style={{ fontWeight: 'bold', textAlign: 'left' }}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className='test-row-container'>
            <td>Mean</td>
            <td>{formatStat(baseMean)}</td>
            <td>{formatStat(newMean)}</td>
            <td></td>
          </tr>
          <tr className='test-row-container'>
            <td>Median</td>
            <td>{formatStat(baseMedian)}</td>
            <td>{formatStat(newMedian)}</td>
            <td></td>
          </tr>
          <tr className='test-row-container'>
            <td>Variance</td>
            <td>{formatStat(baseVariance)}</td>
            <td>{formatStat(newVariance)}</td>
            <td></td>
          </tr>
          <tr className='test-row-container'>
            <td>Standard Deviation</td>
            <td>{formatStat(baseStandardDev)}</td>
            <td>{formatStat(newStandardDev)}</td>
            <td></td>
          </tr>
          <tr className='test-row-container'>
            <td>Min</td>
            <td>{formatStat(baseMin)}</td>
            <td>{formatStat(newMin)}</td>
            <td></td>
          </tr>
          <tr className='test-row-container' style={{ marginBottom: 2 }}>
            <td>Max</td>
            <td>{formatStat(baseMax)}</td>
            <td>{formatStat(newMax)}</td>
            <td></td>
          </tr>
          <tr className='test-label-row'>
            <td>Normality Test</td>
          </tr>
          <tr className='test-row-container'>
            <td>Shapiro-Wilk</td>
            <td>{baseShapiroWilkPVal}</td>
            <td>{newShapiroWilkPVal}</td>
            <td
              style={{
                gridTemplateColumns: '1fr',
                display: 'grid',
                gap: 1,
                flexDirection: 'column',
              }}
            >
              {baseShapiroWilkInterpretation}
              <br />
              {newShapiroWilkInterpretation}
            </td>
          </tr>
          <tr className='test-label-row'>
            <td>Goodness of Fit Test</td>
          </tr>
          <tr className='test-row-container'>
            <td>Kolmogorov-Smirnov Test</td>
            <td></td>
            <td></td>
            <td>{`${result?.ks_test?.interpretation ?? ''}`}</td>
          </tr>
        </tbody>
      </table>
    </Box>
  );
};
